import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { EditorView, basicSetup } from "codemirror";
import { markdown } from "@codemirror/lang-markdown";
import { EditorState, Compartment } from "@codemirror/state";
import { oneDark } from "@codemirror/theme-one-dark";
import { useTheme } from "../context/ThemeContext";
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from "@heroicons/react/24/outline";
import { SegmentedControl } from "./ui/SegmentedControl";
import { Badge } from "./ui/Badge";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  isGenerating?: boolean;
  className?: string;
}

// Min height ≈ 6 lines (Tailwind spacing.48 = 192px)
const MIN_HEIGHT = 192;

export default function MarkdownEditor({
  value,
  onChange,
  label,
  placeholder = "Write Markdown here…",
  isGenerating = false,
  className = "",
}: MarkdownEditorProps) {
  const { theme } = useTheme();
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [focusMode, setFocusMode] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const readOnlyCompartment = useRef(new Compartment());

  // Create / recreate editor when mode changes
  useEffect(() => {
    if (!editorRef.current || mode !== "edit") return;

    const view = new EditorView({
      doc: value,
      extensions: [
        basicSetup,
        markdown(),
        EditorView.lineWrapping,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            onChange(update.state.doc.toString());
          }
        }),
        ...(theme === "dark" ? [oneDark] : []),
        EditorView.theme({
          "&": {
            border: "1px solid rgb(226 232 240)",
            borderRadius: "6px",
            minHeight: `${MIN_HEIGHT}px`,
          },
          ".cm-scroller": { overflow: "visible" },
          ".cm-content": { minHeight: `${MIN_HEIGHT}px` },
        }),
        readOnlyCompartment.current.of(EditorState.readOnly.of(isGenerating)),
      ],
      parent: editorRef.current,
    });

    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, theme]);

  // Dynamically update readOnly via Compartment (no rebuild needed)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: readOnlyCompartment.current.reconfigure(
        EditorState.readOnly.of(isGenerating)
      ),
    });
  }, [isGenerating]);

  // Sync external value changes into editor
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current !== value) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: value },
      });
    }
  }, [value]);

  // Escape closes Focus Mode
  useEffect(() => {
    if (!focusMode) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setFocusMode(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [focusMode]);

  return (
    <div
      className={[
        "flex flex-col gap-2",
        focusMode ? "fixed inset-0 z-50 bg-white dark:bg-slate-900 p-4 overflow-auto" : "",
        className,
      ].filter(Boolean).join(" ")}
      data-focus-mode={focusMode ? "true" : undefined}
      data-editor-mode={mode}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        {label && (
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {label}
          </span>
        )}
        <div className="flex items-center gap-2 ml-auto">
          {isGenerating && (
            <Badge variant="warning">Generating…</Badge>
          )}
          <SegmentedControl
            options={[
              { value: "edit", label: "Edit" },
              { value: "preview", label: "Preview" },
            ]}
            value={mode}
            onChange={setMode}
            disabled={isGenerating}
            aria-label="Document view mode"
          />
          <button
            type="button"
            aria-label={focusMode ? "Exit focus mode" : "Enter focus mode"}
            onClick={() => setFocusMode((v) => !v)}
            className="inline-flex items-center justify-center p-1.5 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 min-h-[44px] min-w-[44px]"
          >
            {focusMode ? (
              <ArrowsPointingInIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <ArrowsPointingOutIcon className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Content area */}
      <div
        className={[
          "transition-opacity duration-200",
          isGenerating ? "opacity-50 pointer-events-none" : "",
        ].filter(Boolean).join(" ")}
        data-editor-content
        data-generating={isGenerating ? "true" : undefined}
      >
        {/* CodeMirror editor — shown in edit mode */}
        {mode === "edit" && (
          <div
            ref={editorRef}
            className="min-h-48"
            aria-label={label ? `${label} editor` : "editor"}
          />
        )}

        {/* Live preview — shown in preview mode only */}
        {mode === "preview" && (
          <div
            className="pdf-preview min-h-48 p-3 border border-slate-200 dark:border-slate-700 rounded-md overflow-visible"
            aria-label={label ? `${label} preview` : "preview"}
          >
            {value ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
            ) : (
              <p className="text-slate-400">{placeholder}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
